<template>
  <a-modal wrap-class-name="cdk-dialog" :open="open" title="登录易游，继续兑换" :footer="null" :width="420" @cancel="close">
    <a-tabs v-model:active-key="mode" @change="refreshCaptcha">
      <a-tab-pane key="login" tab="登录" />
      <a-tab-pane key="register" tab="注册" />
    </a-tabs>
    <a-form layout="vertical" :model="{ phone, password, captcha, smsCode }" @finish="submit">
      <a-form-item :label="mode === 'register' ? '手机号码' : '用户名 / 手机号'">
        <a-input v-model:value="phone" :autocomplete="mode === 'register' ? 'tel' : 'username'" :inputmode="mode === 'register' ? 'tel' : 'text'" :maxlength="mode === 'register' ? 11 : 150" :placeholder="mode === 'register' ? '请输入手机号码' : '请输入用户名或手机号'" />
      </a-form-item>
      <a-form-item label="密码">
        <a-input-password v-model:value="password" :autocomplete="mode === 'login' ? 'current-password' : 'new-password'" placeholder="请输入密码" />
      </a-form-item>
      <a-form-item v-if="mode === 'register'" label="确认密码">
        <a-input-password v-model:value="confirmPassword" autocomplete="new-password" placeholder="请再次输入密码" />
      </a-form-item>
      <a-form-item label="图片验证码">
        <div class="captcha-line">
          <a-input v-model:value="captcha" autocomplete="off" placeholder="请输入图片验证码" />
          <button type="button" class="captcha-image" aria-label="刷新图片验证码" @click="refreshCaptcha">
            <img v-if="captchaUrl" :src="captchaUrl" alt="图片验证码，点击刷新" />
            <span v-else>{{ captchaLoading ? '加载中…' : '点击获取' }}</span>
          </button>
        </div>
      </a-form-item>
      <a-form-item v-if="mode === 'register'" label="短信验证码">
        <div class="captcha-line">
          <a-input v-model:value="smsCode" autocomplete="one-time-code" placeholder="请输入短信验证码" />
          <a-button :loading="smsBusy" :disabled="countdown > 0 || captchaLoading || !captchaId" @click="sendSms">
            {{ countdown ? countdown + '秒后重试' : '获取验证码' }}
          </a-button>
        </div>
      </a-form-item>
      <a-checkbox v-model:checked="agreed">
        我已阅读并同意
        <a href="/helpCenter?id=7" target="_blank" rel="noopener">服务协议</a>和
        <a href="/helpCenter?id=8" target="_blank" rel="noopener">隐私协议</a>
      </a-checkbox>
      <a-button class="submit" type="primary" html-type="submit" block :loading="busy" :disabled="captchaLoading || !captchaId">
        {{ mode === 'login' ? '登录并继续' : '注册并继续' }}
      </a-button>
      <p class="hint">忘记密码请联系页面右侧在线客服。</p>
    </a-form>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { cdkHttp } from "@/api/cdk";

const store = useStore();
const open = computed(() => store.state.authModal.isLoginModalVisible);
const mode = computed({
  get: () => store.state.loginModalType,
  set: (value) => store.commit("SET_LOGINMODALTYPE", value),
});
const phone = ref(""), password = ref(""), confirmPassword = ref(""), captcha = ref("");
const smsCode = ref(""), captchaUrl = ref(""), agreed = ref(false);
const captchaId = ref(""), captchaLoading = ref(false);
const busy = ref(false), smsBusy = ref(false), countdown = ref(0);
let timer, captchaSequence = 0;

function close() {
  store.dispatch("authModal/hideLoginModal");
}
async function refreshCaptcha() {
  const sequence = ++captchaSequence;
  captcha.value = "";
  captchaId.value = "";
  captchaLoading.value = true;
  if (captchaUrl.value) URL.revokeObjectURL(captchaUrl.value);
  captchaUrl.value = "";
  try {
    const res = await cdkHttp.get("userauth/get_image_captcha/", {
      params: { source: mode.value === "register" ? "reg" : "login", time: Date.now() }, responseType: "blob",
    });
    if (sequence !== captchaSequence) return;
    const id = res.headers["x-captcha-uuid"];
    if (!id) throw new Error("missing captcha id");
    captchaId.value = id;
    captchaUrl.value = URL.createObjectURL(res.data);
  } catch {
    if (sequence === captchaSequence) message.error("验证码获取失败，请点击重试");
  } finally {
    if (sequence === captchaSequence) captchaLoading.value = false;
  }
}
async function sendSms() {
  if (smsBusy.value || countdown.value || captchaLoading.value || !captchaId.value) return;
  if (!/^\d{11}$/.test(phone.value) || !captcha.value) return message.warning("请填写手机号和图片验证码");
  smsBusy.value = true;
  try {
    const res = await cdkHttp.post("userauth/get_sms_code/", { phone: phone.value.trim(), captcha: captcha.value.trim(), source: "reg" }, { headers: { "x-captcha-uuid": captchaId.value } });
    if (res.data.status !== 10000) throw new Error(res.data.msg);
    countdown.value = 60;
    clearInterval(timer);
    timer = setInterval(() => { if (--countdown.value <= 0) clearInterval(timer); }, 1000);
    message.success("短信已发送");
  } catch (e) {
    message.error(e.response?.data?.msg || e.message || "短信发送失败");
    refreshCaptcha();
  } finally {
    smsBusy.value = false;
  }
}
async function submit() {
  if (busy.value || captchaLoading.value || !captchaId.value) return;
  if (!phone.value.trim() || !password.value || !captcha.value.trim()) return message.warning("请填写账号、密码和图片验证码");
  if (mode.value === "register" && !/^\d{11}$/.test(phone.value.trim())) return message.warning("请输入11位手机号码");
  if (!agreed.value) return message.warning("请先阅读并同意服务协议及隐私协议");
  if (mode.value === "register" && (password.value !== confirmPassword.value || !smsCode.value)) {
    return message.warning("请确认两次密码一致并填写短信验证码");
  }
  busy.value = true;
  try {
    const registering = mode.value === "register";
    const body = registering ? {
      phone: phone.value.trim(), password: password.value, confirmPassword: confirmPassword.value,
      captcha: captcha.value.trim(), smsCode: smsCode.value.trim(), agreeTerms: true,
      inviteCode: localStorage.getItem("user_invitation_code") || "",
    } : { username: phone.value.trim(), password: password.value, captcha: captcha.value.trim(), remember: true };
    const res = await cdkHttp.post(registering ? "userauth/register/phone/" : "userauth/login/", body, { headers: { "x-captcha-uuid": captchaId.value } });
    if (res.data.status !== 10000) throw new Error(res.data.msg || "登录失败");
    localStorage.setItem("access_token", res.data.data.token);
    const info = await cdkHttp.post("web/user_info/");
    if (info.data.status !== 10000) throw new Error(info.data.msg || "用户信息读取失败");
    store.commit("SET_USER", info.data.data);
    close();
    store.commit("SET_AUTHENTICATED", true);
    password.value = confirmPassword.value = smsCode.value = "";
  } catch (e) {
    message.error(e.response?.data?.msg || e.message || "登录失败");
    refreshCaptcha();
  } finally {
    busy.value = false;
  }
}
watch(open, (value) => {
  if (value) refreshCaptcha();
  else { ++captchaSequence; captchaId.value = ""; captchaLoading.value = false; }
}, { immediate: true });
onBeforeUnmount(() => {
  ++captchaSequence;
  clearInterval(timer);
  if (captchaUrl.value) URL.revokeObjectURL(captchaUrl.value);
});
</script>

<style scoped>
.captcha-line { display: flex; gap: 12px; }
.captcha-image { border: 0; background: #fff7ef; min-width: 160px; padding: 0; cursor: pointer; border-radius: 6px; }
.captcha-image img { width: 160px; height: 60px; object-fit: contain; }
.submit { margin-top: 22px; height: 42px; }
.hint { color: #999; font-size: 12px; text-align: center; margin-top: 12px; }
</style>
